/**
 * Carding Quick Entry — mirrors the LMW CARD LC 363 machine display.
 * Sprint 0146.
 *
 * The supervisor walks up to a card, looks at the touchscreen, and
 * types the 11 numbers shown into matching fields on this page in the
 * same order. Server computes everything else: ktex, can content,
 * theoretical/expected production, cans produced, hank↔kg consistency.
 * Any fault counter > 0 opens a "Why?" drawer for root cause +
 * corrective action — closes the quality loop at entry time instead
 * of later phone calls.
 *
 * Designed for tablets: big tap targets, numeric keypad mode, save
 * snaps to the next machine in the section.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Alert, Button, Card, Col, DatePicker, Divider, Drawer, Form, Input,
  InputNumber, Row, Select, Space, Statistic, Tag, Tooltip, Typography,
  message,
} from 'antd'
import {
  CheckCircleOutlined, SaveOutlined, WarningOutlined,
} from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'

import { cardingApi } from '@/features/production/cardingApi'
import { usePlantId } from '@/shared/hooks/usePlantId'
import apiClient from '@/shared/api/client'

const { Title, Text, Paragraph } = Typography

const FAULT_TYPES = [
  'DOUBLE_LAP', 'OTHER_FAULTS', 'SLIVER_BREAK', 'CHUTE_FAULTS', 'DOFFS',
] as const
type FaultType = typeof FAULT_TYPES[number]

interface MachineLite { id: string; machine_code: string; machine_name: string }
interface ShiftLite { id: string; shift_code: string; shift_name: string }
interface CountLite { id: string; count_code: string; count_ne: number }

// Common root-cause chips per fault type — supervisor taps a chip
// instead of typing. Free text always available.
const ROOT_CAUSE_CHIPS: Record<FaultType, string[]> = {
  DOUBLE_LAP: ['Cotton sticky', 'Chute pressure low', 'Feed roller jam', 'Photocell dirty'],
  SLIVER_BREAK: ['Trumpet blocked', 'Draft too high', 'Sliver too thin', 'Calender slip'],
  CHUTE_FAULTS: ['Chute pressure', 'Photocell dirty', 'Flap stuck', 'Bridging'],
  OTHER_FAULTS: ['Power dip', 'Mechanical', 'Electrical', 'Operator'],
  DOFFS: [],  // no root-cause needed for doffs — it's a productivity counter
}

const CORRECTIVE_CHIPS: Record<FaultType, string[]> = {
  DOUBLE_LAP: ['Cleaned chute', 'Adjusted pressure', 'Cleared jam'],
  SLIVER_BREAK: ['Cleaned trumpet', 'Adjusted draft', 'Replaced sliver guide'],
  CHUTE_FAULTS: ['Reset pressure', 'Cleaned sensor', 'Freed flap'],
  OTHER_FAULTS: ['Restarted', 'Called maintenance', 'Logged for next shift'],
  DOFFS: [],
}

export default function CardingQuickEntryPage() {
  const { plantId } = usePlantId()
  const [form] = Form.useForm()

  const [machineId, setMachineId] = useState<string | undefined>()
  const [shiftId, setShiftId] = useState<string | undefined>()
  const [countId, setCountId] = useState<string | undefined>()
  const [logDate, setLogDate] = useState<Dayjs>(dayjs())

  // Display fields from LMW (mirror the screen)
  const [hanks, setHanks] = useState<number | null>(null)
  const [kgs, setKgs] = useState<number | null>(null)
  const [runH, setRunH] = useState<number | null>(null)
  const [runM, setRunM] = useState<number | null>(null)
  const [idleH, setIdleH] = useState<number | null>(null)
  const [idleM, setIdleM] = useState<number | null>(null)
  const [pfH, setPfH] = useState<number | null>(null)
  const [pfM, setPfM] = useState<number | null>(null)
  const [mcEff, setMcEff] = useState<number | null>(null)

  // Fault counters
  const [doubleLap, setDoubleLap] = useState<number>(0)
  const [otherFaults, setOtherFaults] = useState<number>(0)
  const [sliverBreak, setSliverBreak] = useState<number>(0)
  const [chuteFaults, setChuteFaults] = useState<number>(0)
  const [doffs, setDoffs] = useState<number>(0)

  // Fault root-cause state (filled via drawer)
  type FaultMeta = { root_cause: string | null; corrective_action: string | null }
  const [faultMeta, setFaultMeta] = useState<Record<FaultType, FaultMeta>>({
    DOUBLE_LAP: { root_cause: null, corrective_action: null },
    OTHER_FAULTS: { root_cause: null, corrective_action: null },
    SLIVER_BREAK: { root_cause: null, corrective_action: null },
    CHUTE_FAULTS: { root_cause: null, corrective_action: null },
    DOFFS: { root_cause: null, corrective_action: null },
  })
  // Which fault's "Why?" drawer is open right now (null = closed).
  const [whyOpen, setWhyOpen] = useState<FaultType | null>(null)
  const [remarks, setRemarks] = useState('')

  // ── Master-data loads ───────────────────────────────────────────────
  const machinesQ = useQuery({
    enabled: !!plantId,
    queryKey: ['quick-machines-carding', plantId],
    queryFn: async () => {
      const r = await apiClient.get('/masters/machines', {
        params: { plant_id: plantId, is_active: true, size: 500 },
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return raw
        .filter((m) => (m.machine_type ?? m.section ?? '').toUpperCase() === 'CARDING')
        .map((m): MachineLite => ({
          id: m.id,
          machine_code: m.machine_code ?? m.machine_id_str ?? '',
          machine_name: m.machine_name ?? m.name ?? '',
        }))
        .sort((a, b) => a.machine_code.localeCompare(b.machine_code, undefined, { numeric: true }))
    },
  })

  const shiftsQ = useQuery({
    enabled: !!plantId,
    queryKey: ['quick-shifts', plantId],
    queryFn: async () => {
      const r = await apiClient.get('/masters/shifts', {
        params: { plant_id: plantId, is_active: true },
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return raw.map((s): ShiftLite => ({
        id: s.id, shift_code: s.shift_code, shift_name: s.shift_name,
      }))
    },
  })

  const countsQ = useQuery({
    queryKey: ['quick-counts'],
    queryFn: async () => {
      const r = await apiClient.get('/masters/counts', { params: { size: 500 } })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return raw.map((c): CountLite => ({
        id: c.id, count_code: c.count_code ?? '', count_ne: c.count_ne ?? 0,
      }))
    },
  })

  // Active standard for the selected machine — gives us sliver hank for
  // the live cross-check.
  const stdQ = useQuery({
    enabled: !!plantId && !!machineId,
    queryKey: ['quick-std', plantId, machineId, countId],
    queryFn: async () => {
      const params: Record<string, unknown> = {
        plant_id: plantId, machine_id: machineId, active_only: true,
      }
      if (countId) params.count_id = countId
      const r = await apiClient.get('/carding/settings', { params })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const arr: any[] = Array.isArray(r.data) ? r.data : []
      return arr[0] ?? null
    },
  })

  // ── Local hank↔kg cross-check (matches server math) ────────────────
  const liveCrossCheck = useMemo(() => {
    const stdHank = stdQ.data?.card_sliver_hank
      ? Number(stdQ.data.card_sliver_hank) : null
    if (!stdHank || !kgs || !hanks) return null
    // Hanks = kg × hank / 0.4535
    const expected = (kgs * stdHank) / 0.4535
    const dev = ((hanks - expected) / expected) * 100
    const impliedHank = (hanks * 0.4535) / kgs
    return {
      expected_hanks: expected,
      deviation_pct: dev,
      implied_hank: impliedHank,
      status: Math.abs(dev) <= 2 ? 'MATCH' : 'MISMATCH',
    }
  }, [kgs, hanks, stdQ.data])

  // ── Submit ──────────────────────────────────────────────────────────
  const saveMut = useMutation({
    mutationFn: cardingApi.quickEntry,
    onSuccess: (res) => {
      message.success(
        `Saved ${res.log_no} · ${res.alerts_raised} alert${res.alerts_raised === 1 ? '' : 's'} raised`
      )
      // Auto-advance to next machine in the section so supervisor can
      // walk through CD-01 → CD-02 → ... without re-picking each time.
      const list = machinesQ.data ?? []
      const idx = list.findIndex((m) => m.id === machineId)
      if (idx >= 0 && idx < list.length - 1) {
        setMachineId(list[idx + 1].id)
      }
      // Reset numeric fields but keep machine / shift / date / count
      setHanks(null); setKgs(null)
      setRunH(null); setRunM(null)
      setIdleH(null); setIdleM(null)
      setPfH(null); setPfM(null)
      setMcEff(null)
      setDoubleLap(0); setOtherFaults(0); setSliverBreak(0)
      setChuteFaults(0); setDoffs(0)
      setFaultMeta({
        DOUBLE_LAP: { root_cause: null, corrective_action: null },
        OTHER_FAULTS: { root_cause: null, corrective_action: null },
        SLIVER_BREAK: { root_cause: null, corrective_action: null },
        CHUTE_FAULTS: { root_cause: null, corrective_action: null },
        DOFFS: { root_cause: null, corrective_action: null },
      })
      setRemarks('')
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => {
      message.error(e?.response?.data?.detail ?? 'Save failed')
    },
  })

  const faultsForSave = () => {
    const out: Array<{
      fault_type: FaultType; count: number
      root_cause?: string | null; corrective_action?: string | null
    }> = []
    const pairs: Array<[FaultType, number]> = [
      ['DOUBLE_LAP', doubleLap],
      ['OTHER_FAULTS', otherFaults],
      ['SLIVER_BREAK', sliverBreak],
      ['CHUTE_FAULTS', chuteFaults],
      ['DOFFS', doffs],
    ]
    for (const [t, c] of pairs) {
      if (c > 0) {
        out.push({
          fault_type: t, count: c,
          root_cause: faultMeta[t].root_cause,
          corrective_action: faultMeta[t].corrective_action,
        })
      }
    }
    return out
  }

  // Block save if any non-DOFFS fault > 0 lacks a root cause
  const missingWhy: FaultType[] = useMemo(() => {
    const out: FaultType[] = []
    const pairs: Array<[FaultType, number]> = [
      ['DOUBLE_LAP', doubleLap],
      ['OTHER_FAULTS', otherFaults],
      ['SLIVER_BREAK', sliverBreak],
      ['CHUTE_FAULTS', chuteFaults],
    ]
    for (const [t, c] of pairs) {
      if (c > 0 && !faultMeta[t].root_cause?.trim()) out.push(t)
    }
    return out
  }, [doubleLap, otherFaults, sliverBreak, chuteFaults, faultMeta])

  const handleSave = () => {
    if (!plantId || !machineId || !shiftId) {
      message.warning('Pick machine and shift first')
      return
    }
    if (missingWhy.length > 0) {
      message.warning(
        `Fill the root cause for: ${missingWhy.join(', ')}. Tap the red faults to open Why?`
      )
      return
    }
    const runMin = (runH ?? 0) * 60 + (runM ?? 0)
    const idleMin = (idleH ?? 0) * 60 + (idleM ?? 0)
    const pfMin = (pfH ?? 0) * 60 + (pfM ?? 0)
    saveMut.mutate({
      plant_id: plantId,
      machine_id: machineId,
      shift_id: shiftId,
      log_date: logDate.format('YYYY-MM-DD'),
      count_id: countId ?? null,
      hanks_produced: hanks,
      production_kg: kgs,
      run_time_minutes: runMin || null,
      idle_time_minutes: idleMin || null,
      power_fail_minutes: pfMin || null,
      machine_efficiency_pct: mcEff,
      faults: faultsForSave(),
      remarks: remarks || null,
    })
  }

  // ── Render ──────────────────────────────────────────────────────────
  const stdHank = stdQ.data?.card_sliver_hank
  const numField = (
    value: number | null,
    setter: (v: number | null) => void,
    placeholder = '',
    step = 1,
  ) => (
    <InputNumber
      value={value}
      onChange={(v) => setter(v as number | null)}
      placeholder={placeholder}
      style={{ width: '100%', height: 44, fontSize: 16 }}
      step={step}
      inputMode="decimal"
    />
  )

  return (
    <div style={{ padding: 16, maxWidth: 1100, margin: '0 auto' }}>
      <Title level={3} style={{ marginBottom: 4 }}>Carding Quick Entry</Title>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Mirror the LMW machine display, fault by fault. Save snaps to the
        next machine in your section.
      </Paragraph>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Text strong>Machine</Text>
            <Select
              showSearch
              style={{ width: '100%', marginTop: 4 }}
              value={machineId}
              onChange={setMachineId}
              placeholder="Pick CD-01..."
              options={(machinesQ.data ?? []).map((m) => ({
                label: `${m.machine_code} — ${m.machine_name}`, value: m.id,
              }))}
              filterOption={(input, opt) =>
                (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={12} sm={6}>
            <Text strong>Shift</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              value={shiftId}
              onChange={setShiftId}
              placeholder="A / B / C"
              options={(shiftsQ.data ?? []).map((s) => ({
                label: s.shift_code, value: s.id,
              }))}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Text strong>Date</Text>
            <DatePicker
              value={logDate}
              onChange={(d) => d && setLogDate(d)}
              style={{ width: '100%', marginTop: 4 }}
              format="DD-MMM-YYYY"
              allowClear={false}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Text strong>Count</Text>
            <Select
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              value={countId}
              onChange={setCountId}
              placeholder="(any)"
              options={(countsQ.data ?? []).map((c) => ({
                label: `${c.count_code} (${c.count_ne}s)`, value: c.id,
              }))}
            />
          </Col>
        </Row>
      </Card>

      <Row gutter={12}>
        <Col xs={24} lg={14}>
          <Card size="small" title="Production (from LMW)" style={{ marginBottom: 12 }}>
            <Row gutter={12}>
              <Col span={12}>
                <Text>Hank (production)</Text>
                {numField(hanks, setHanks, '0050.31', 0.01)}
              </Col>
              <Col span={12}>
                <Text>Kgs</Text>
                {numField(kgs, setKgs, '0207.44', 0.01)}
              </Col>
            </Row>
            <Divider style={{ margin: '12px 0' }} />
            <Row gutter={8}>
              <Col span={8}>
                <Text>Run Time</Text>
                <Space.Compact style={{ width: '100%' }}>
                  {numField(runH, setRunH, 'hh')}
                  {numField(runM, setRunM, 'mm')}
                </Space.Compact>
              </Col>
              <Col span={8}>
                <Text>Idle Time</Text>
                <Space.Compact style={{ width: '100%' }}>
                  {numField(idleH, setIdleH, 'hh')}
                  {numField(idleM, setIdleM, 'mm')}
                </Space.Compact>
              </Col>
              <Col span={8}>
                <Text>Power Fail</Text>
                <Space.Compact style={{ width: '100%' }}>
                  {numField(pfH, setPfH, 'hh')}
                  {numField(pfM, setPfM, 'mm')}
                </Space.Compact>
              </Col>
            </Row>
            <Row gutter={12} style={{ marginTop: 12 }}>
              <Col span={12}>
                <Text>M/c Eff (%)</Text>
                {numField(mcEff, setMcEff, '58.49', 0.01)}
              </Col>
            </Row>
          </Card>

          <Card size="small" title="Fault counters" style={{ marginBottom: 12 }}>
            <FaultRow
              label="Double Lap" count={doubleLap}
              onChange={setDoubleLap}
              hasWhy={!!faultMeta.DOUBLE_LAP.root_cause}
              onWhy={() => setWhyOpen('DOUBLE_LAP')}
            />
            <FaultRow
              label="Other Faults" count={otherFaults}
              onChange={setOtherFaults}
              hasWhy={!!faultMeta.OTHER_FAULTS.root_cause}
              onWhy={() => setWhyOpen('OTHER_FAULTS')}
            />
            <FaultRow
              label="Sliver Break" count={sliverBreak}
              onChange={setSliverBreak}
              hasWhy={!!faultMeta.SLIVER_BREAK.root_cause}
              onWhy={() => setWhyOpen('SLIVER_BREAK')}
            />
            <FaultRow
              label="Chute Faults" count={chuteFaults}
              onChange={setChuteFaults}
              hasWhy={!!faultMeta.CHUTE_FAULTS.root_cause}
              onWhy={() => setWhyOpen('CHUTE_FAULTS')}
            />
            <FaultRow
              label="No of Doffs" count={doffs}
              onChange={setDoffs}
              hasWhy={true}  // doffs don't need a why
              onWhy={() => {}}
              noWhyButton
            />
          </Card>

          <Card size="small" title="Remarks (optional)" style={{ marginBottom: 12 }}>
            <Input.TextArea
              rows={2} value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Any notes for next shift / supervisor…"
            />
          </Card>

          <Button
            type="primary" block size="large" icon={<SaveOutlined />}
            loading={saveMut.isPending}
            disabled={!machineId || !shiftId || missingWhy.length > 0}
            onClick={handleSave}
            style={{ height: 56, fontSize: 18 }}
          >
            {missingWhy.length > 0
              ? `Fill "Why?" for ${missingWhy.length} fault${missingWhy.length === 1 ? '' : 's'} first`
              : 'Save & next machine'}
          </Button>
        </Col>

        {/* Live derivations + cross-checks */}
        <Col xs={24} lg={10}>
          <Card size="small" title="Live cross-check" style={{ marginBottom: 12 }}>
            {stdHank ? (
              <Text type="secondary">
                Standard sliver hank: <Tag>{stdHank} Ne</Tag>
              </Text>
            ) : (
              <Alert
                type="warning" showIcon
                message="No active standard for this machine/count"
                description="Cross-checks limited. Set a Carding Setting first."
              />
            )}
            {liveCrossCheck && (
              <div style={{ marginTop: 12 }}>
                <Statistic
                  title="Expected hanks from kg"
                  value={liveCrossCheck.expected_hanks}
                  precision={2}
                  suffix={
                    liveCrossCheck.status === 'MATCH'
                      ? <Tag color="green" icon={<CheckCircleOutlined />}>MATCH</Tag>
                      : <Tag color="orange" icon={<WarningOutlined />}>MISMATCH</Tag>
                  }
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Deviation {liveCrossCheck.deviation_pct.toFixed(2)}%
                  {' · '}
                  Implied sliver hank from your inputs: {liveCrossCheck.implied_hank.toFixed(4)} Ne
                  {liveCrossCheck.status === 'MISMATCH' &&
                    ' — schedule a QC sample, sliver may have drifted'}
                </Text>
              </div>
            )}
          </Card>

          <Card size="small" title="Auto-derived (server)">
            <Paragraph type="secondary" style={{ fontSize: 12 }}>
              Computed on Save from your inputs + the active standard
              (sliver hank, can length, std efficiency). Comes back in
              the response, written to the production log.
            </Paragraph>
            <ul style={{ fontSize: 13, marginBottom: 0 }}>
              <li>Sliver ktex</li>
              <li>Can content (kg per can)</li>
              <li>Theoretical kg (perfect run)</li>
              <li>Expected kg (theoretical × std eff)</li>
              <li>Actual efficiency vs theoretical</li>
              <li>Cans produced</li>
            </ul>
          </Card>
        </Col>
      </Row>

      {/* "Why?" drawer — fires whenever a fault > 0 is tapped */}
      <Drawer
        title={
          <Space>
            <WarningOutlined style={{ color: '#fa8c16' }} />
            <span>Why? — {whyOpen?.replace('_', ' ')}</span>
          </Space>
        }
        open={!!whyOpen}
        onClose={() => setWhyOpen(null)}
        width={420}
      >
        {whyOpen && (
          <WhyForm
            faultType={whyOpen}
            meta={faultMeta[whyOpen]}
            onSave={(meta) => {
              setFaultMeta({ ...faultMeta, [whyOpen]: meta })
              setWhyOpen(null)
            }}
          />
        )}
      </Drawer>
    </div>
  )
}

// ─── Fault row ──────────────────────────────────────────────────────

function FaultRow({
  label, count, onChange, hasWhy, onWhy, noWhyButton = false,
}: {
  label: string
  count: number
  onChange: (n: number) => void
  hasWhy: boolean
  onWhy: () => void
  noWhyButton?: boolean
}) {
  const needsWhy = count > 0 && !hasWhy && !noWhyButton
  return (
    <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
      <Col span={10}>
        <Text>{label}</Text>
      </Col>
      <Col span={8}>
        <InputNumber
          min={0}
          value={count}
          onChange={(v) => onChange(Number(v ?? 0))}
          style={{ width: '100%', height: 40, fontSize: 16 }}
          inputMode="numeric"
        />
      </Col>
      <Col span={6}>
        {!noWhyButton && count > 0 && (
          <Button
            danger={needsWhy}
            size="small"
            type={needsWhy ? 'primary' : 'default'}
            onClick={onWhy}
            block
          >
            {hasWhy ? '✓ Why?' : 'Why?'}
          </Button>
        )}
      </Col>
    </Row>
  )
}

// ─── Why? form ──────────────────────────────────────────────────────

function WhyForm({
  faultType, meta, onSave,
}: {
  faultType: FaultType
  meta: { root_cause: string | null; corrective_action: string | null }
  onSave: (meta: { root_cause: string | null; corrective_action: string | null }) => void
}) {
  const [cause, setCause] = useState(meta.root_cause ?? '')
  const [action, setAction] = useState(meta.corrective_action ?? '')

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <div>
        <Text strong>Root cause</Text>
        <Space wrap size={[6, 6]} style={{ marginTop: 6, marginBottom: 6 }}>
          {ROOT_CAUSE_CHIPS[faultType].map((c) => (
            <Tag.CheckableTag
              key={c}
              checked={cause === c}
              onChange={() => setCause(c)}
            >
              {c}
            </Tag.CheckableTag>
          ))}
        </Space>
        <Input.TextArea
          rows={2}
          value={cause}
          onChange={(e) => setCause(e.target.value)}
          placeholder="Tap a chip or type your own"
        />
      </div>

      <div>
        <Text strong>Corrective action taken</Text>
        <Space wrap size={[6, 6]} style={{ marginTop: 6, marginBottom: 6 }}>
          {CORRECTIVE_CHIPS[faultType].map((c) => (
            <Tag.CheckableTag
              key={c}
              checked={action === c}
              onChange={() => setAction(c)}
            >
              {c}
            </Tag.CheckableTag>
          ))}
        </Space>
        <Input.TextArea
          rows={2}
          value={action}
          onChange={(e) => setAction(e.target.value)}
          placeholder="What did you do about it?"
        />
      </div>

      <Tooltip title="Root cause is required when fault count > 0. Corrective action is recommended but optional.">
        <Button
          type="primary"
          block
          disabled={!cause.trim()}
          onClick={() => onSave({
            root_cause: cause.trim() || null,
            corrective_action: action.trim() || null,
          })}
        >
          Save reason
        </Button>
      </Tooltip>
    </Space>
  )
}
